import type { Property } from '~/core/entities/property';
import type { PropertyFilters } from '~/core/entities/property/filters';

import { logger } from '~/core/service/logger';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';

interface UsePropertiesListingOptions {
	/** Initial filters for the search */
	initialFilters?: Partial<PropertyFilters>;
	/** Number of items per page */
	perPage?: number;
	/** Whether to include H3 clusters in response */
	includeH3Clusters?: boolean;
	/** Whether to mark user favorites */
	markUserWishlists?: boolean;
	/** Function to build filters dynamically */
	filtersBuilder?: () => PropertyFilters;
	/** Custom data loader function */
	dataLoader?: (page: number) => Promise<{
		properties: Property[];
		hasMore: boolean;
		totalCount: number;
		totalPages: number;
	}>;
}

interface UsePropertiesListingReturn {
	properties: Ref<Property[]>;
	isLoading: Ref<boolean>;
	hasMore: Ref<boolean>;
	isEmpty: Ref<boolean>;
	error: Ref<Error | null>;
	currentPage: Ref<number>;
	totalCount: Ref<number>;
	totalPages: Ref<number>;
	loadProperties: () => Promise<void>;
	loadNextPage: () => Promise<void>;
	refresh: () => Promise<void>;
	reset: () => void;
}

/**
 * Generic composable for property listing with pagination
 * Can be used for different types of listings: similar properties, curated for you, etc.
 */
export function usePropertiesListing(
	options: UsePropertiesListingOptions = {}
): UsePropertiesListingReturn {
	const {
		initialFilters = {},
		perPage = 12,
		includeH3Clusters = false,
		markUserWishlists = false,
		filtersBuilder,
		dataLoader
	} = options;

	const { user } = useUserSession();
	const getCookieData = useCookieData();

	// Reactive state
	const properties = ref<Property[]>([]);
	const isLoading = ref(false);
	const hasMore = ref(true);
	const error = ref<Error | null>(null);
	const currentPage = ref(1);
	const totalCount = ref(0);
	const totalPages = ref(0);

	// Computed state
	const isEmpty = computed(
		() => properties.value.length === 0 && !isLoading.value && !error.value
	);

	// Default data loader using the standard list endpoint
	const defaultDataLoader = async (page: number) => {
		const filters = buildFilters(page);

		const [result, repositoryError] = await nuxtPropertiesRepository.list(
			filters,
			getCookieData()
		);

		if (repositoryError) {
			logger.error({
				source: 'usePropertiesListing',
				message: 'Error while fetching properties from repository',
				originalErrorObject: { message: repositoryError.message }
			});

			// Throw the error so it can be caught by the calling function
			throw repositoryError;
		}

		if (!result) {
			logger.error({
				source: 'usePropertiesListing',
				message: 'Error while fetching properties from repository',
				originalErrorObject: { message: 'No result from repository' }
			});

			return { properties: [], hasMore: false, totalCount: 0, totalPages: 0 };
		}

		return {
			properties: result.properties ?? [],
			hasMore:
				(result.pagination?.page ?? 1) < (result.pagination?.totalPages ?? 1),
			totalCount: result.pagination?.filteredCount ?? 0,
			totalPages: result.pagination?.totalPages ?? 0
		};
	};

	/**
	 * Builds final filters by combining initial filters with dynamic filters
	 */
	function buildFilters(
		page: number
	): PropertyFilters & { page: number; perPage: number } {
		const dynamicFilters = filtersBuilder ? filtersBuilder() : {};
		return {
			...initialFilters,
			...dynamicFilters,
			page,
			perPage,
			...(includeH3Clusters && { includeH3Clusters }),
			...(markUserWishlists &&
				user.value?.id && { markUserWishlistsId: user.value.id })
		};
	}

	/**
	 * Loads the first page of properties
	 */
	async function loadProperties(): Promise<void> {
		isLoading.value = true;
		error.value = null;
		currentPage.value = 1;

		try {
			const loader = dataLoader || defaultDataLoader;
			const result = await loader(currentPage.value);

			properties.value = result.properties;
			totalCount.value = result.totalCount;
			hasMore.value = result.hasMore;
			totalPages.value = result.totalPages;
		} catch (err) {
			if (err instanceof Error) {
				error.value = err;
			} else if (err && typeof err === 'object' && 'message' in err) {
				error.value = new Error(err.message as string);
			} else {
				error.value = new Error('Unknown error');
			}
		} finally {
			isLoading.value = false;
		}
	}

	/**
	 * Loads the next page and adds properties to the existing list
	 */
	async function loadNextPage(): Promise<void> {
		if (!hasMore.value || isLoading.value) return;

		isLoading.value = true;
		error.value = null;

		try {
			const nextPage = currentPage.value + 1;
			const loader = dataLoader || defaultDataLoader;
			const result = await loader(nextPage);

			properties.value = [...properties.value, ...result.properties];
			currentPage.value = nextPage;
			hasMore.value = result.hasMore;
			totalCount.value = result.totalCount;
			totalPages.value = result.totalPages;
		} catch (err) {
			if (err instanceof Error) {
				error.value = err;
			} else if (err && typeof err === 'object' && 'message' in err) {
				error.value = new Error(err.message as string);
			} else {
				error.value = new Error('Unknown error');
			}
		} finally {
			isLoading.value = false;
		}
	}

	/**
	 * Reloads the first page (useful for refresh)
	 */
	async function refresh(): Promise<void> {
		reset();
		await loadProperties();
	}

	/**
	 * Resets the composable state
	 */
	function reset(): void {
		properties.value = [];
		currentPage.value = 1;
		hasMore.value = true;
		error.value = null;
		totalCount.value = 0;
		totalPages.value = 0;
	}

	return {
		properties,
		isLoading,
		hasMore,
		isEmpty,
		error,
		currentPage,
		totalCount,
		totalPages,
		loadProperties,
		loadNextPage,
		refresh,
		reset
	};
}
